import { useMemo } from 'react';
import { Button } from '@givebutter/primitives/button';
import { maskDecimal } from '@givebutter/util';
import {
    faArrowsRotate,
    faCheck,
    faDownload,
    faExclamation,
    faEye,
    faLinkSlash,
    faPause,
    faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import { faEllipsisV } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as DetachTransaction from 'assets/js/pages/pledges/DetachTransactionDialog';
import * as DropdownMenu from 'components/dropdown-menu';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { dateFromServer } from 'helpers/dates';
import { Transaction } from 'types/domain';

const PaymentItem = ({ item }: { item: Transaction }) => {
    const { accountId } = useCurrentAccount();

    const status = useMemo(() => {
        switch (item.status_friendly) {
            case 'Paused':
                return {
                    icon: faPause,
                    iconClass: 'bg-gray-500 text-white text-xs',
                    labelClass: 'text-gray-500',
                };
            case 'Cancelled':
            case 'Past due failed':
                return {
                    icon: faTimes,
                    iconClass: 'bg-red-500 text-white text-xs',
                    labelClass: 'text-red-500',
                };
            case 'Refunded':
                return {
                    icon: faArrowsRotate,
                    iconClass: 'bg-white text-gray-500 text-sm',
                    labelClass: 'text-gray-500',
                };
            case 'Past due':
            case 'Disputed':
                return {
                    icon: faExclamation,
                    iconClass: 'bg-orange-500 text-white text-xs',
                    labelClass: 'text-orange-500',
                };
            case 'Pending':
                return {
                    icon: faCheck,
                    iconClass: 'bg-gray-500 text-white text-xs',
                    labelClass: 'text-gray-500',
                };
            default:
                return {
                    icon: faCheck,
                    iconClass: 'bg-green-500 text-white text-xs',
                    labelClass: 'text-green-500',
                };
        }
    }, [item.status_friendly]);

    return (
        <div className="flex gap-3 border-b border-b-gray-200 py-4 last:border-none">
            <div
                className={`mt-1 inline-flex h-4 w-4 items-center justify-center rounded-full ${status.iconClass}`}
            >
                <FontAwesomeIcon icon={status.icon} />
            </div>
            <div className="flex flex-1 items-center justify-between">
                <div>
                    <div className="flex gap-2 text-sm font-medium text-gray-500">
                        <span>
                            <CurrencySymbol />
                            {maskDecimal(item.amount)}
                        </span>
                        <span className="text-gray-400">#{item.number}</span>
                    </div>
                    <div className={`text-sm ${status.labelClass}`}>
                        {item.status_friendly}{' '}
                        {dateFromServer(item.transacted_at).toFormat('MM/dd/yy')}
                    </div>
                </div>
                <div className="flex items-center">
                    <DropdownMenu.Root>
                        <DropdownMenu.Trigger asChild={true}>
                            <Button
                                size={'sm'}
                                variant="transparent"
                                shape={'square'}
                                aria-label="Transaction options"
                            >
                                <FontAwesomeIcon className="text-gray-500" icon={faEllipsisV} />
                            </Button>
                        </DropdownMenu.Trigger>
                        <DropdownMenu.Portal>
                            <DropdownMenu.Content sideOffset={4} align={'end'}>
                                <DropdownMenu.LinkItem
                                    to={`/accounts/${accountId}/transactions/${item.id}`}
                                >
                                    <p className="flex gap-2">
                                        <FontAwesomeIcon icon={faEye} /> View transaction
                                    </p>
                                </DropdownMenu.LinkItem>
                                <DropdownMenu.Item className="!p-0">
                                    <a
                                        href={item.receipt_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex gap-2 p-2 text-gray-900 hover:text-gray-900"
                                    >
                                        <FontAwesomeIcon icon={faDownload} /> Download receipt
                                    </a>
                                </DropdownMenu.Item>
                                <DetachTransaction.Root id={item.id}>
                                    <DetachTransaction.Trigger asChild>
                                        <DropdownMenu.Item
                                            className="flex items-center gap-2 text-red-600"
                                            onSelect={e => {
                                                e.preventDefault();
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faLinkSlash} /> Unlink
                                            transaction
                                        </DropdownMenu.Item>
                                    </DetachTransaction.Trigger>
                                </DetachTransaction.Root>
                            </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                    </DropdownMenu.Root>
                </div>
            </div>
        </div>
    );
};

export { PaymentItem };
